'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { createGroup, getGroup, GroupCreationError, saveGroupNotificationInterest } from '@/lib/quiz/groups';
import { clearCreatorDraft, resolveCreatorDraft, type CreatorDraft } from '@/lib/handoff/creator-draft';
import { getLockedQuestionIds, getLongQuizComparisonIds } from '@/lib/quiz/group-questions';
import { looksLikeEmail } from '@/lib/quiz/email-domain';
import { useLocale } from '@/lib/locale-context';
import { t } from '@/lib/quiz/group-i18n';
import { GROUP_DISPLAY_NAME_MAX, GROUP_MAX_MEMBERS } from '@/types/groups';
import { capturePosthogEvent } from '@/lib/posthog';
import { usePostHog } from 'posthog-js/react';
import { debugError } from '@/lib/debug-logger';
import { parseCreationAttribution } from '@/lib/group/creation-attribution';
import PageShell from '@/components/PageShell';
import Button from '@/components/ui/Button';
import { ArrowLeft } from 'lucide-react';

// Next 16 requires components that read useSearchParams() to be wrapped
// in <Suspense> so the static prerender can bail out cleanly. We split
// the page export to satisfy this: the outer component is a tiny shell,
// the inner does the actual work and reads searchParams.
export default function NewGroupPage() {
  return (
    <Suspense fallback={null}>
      <NewGroupPageInner />
    </Suspense>
  );
}

function NewGroupPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const { locale } = useLocale();

  // ?retake=<oldGroupId> arrives from the 90-day retake reminder email.
  // We prefill the group name with the old group's name + "(retake)" so
  // the new group is recognizable as a follow-up, and show a small
  // banner so the host knows the context.
  const retakeOf = searchParams.get('retake');
  // ?seed=<archetype> arrives from the solo /quiz/results "Take this
  // with friends" CTA. We use it to nudge a more identity-specific
  // group name suggestion ("My Centrism crew") in the placeholder.
  const seedArchetype = searchParams.get('seed');
  // ?h=<token> is the server-side handoff from the results CTA. It carries
  // the creator's quizResponseId + quizType so their slot prefills without
  // a retake, keeping that state out of the URL.
  const handoffToken = searchParams.get('h');

  const [groupName, setGroupName] = useState('');
  const [hostName, setHostName] = useState('');
  const [email, setEmail] = useState('');
  // 'pair' = one-to-many 1:1 (host vs each viewer, uncapped); 'group' = up to GROUP_MAX_MEMBERS.
  const [compareMode, setCompareMode] = useState<'pair' | 'group'>('group');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retakeOriginalName, setRetakeOriginalName] = useState<string | null>(null);
  const [retakeQuestionIds, setRetakeQuestionIds] = useState<string[] | null>(null);
  const [handoffState, setHandoffState] = useState<{
    token: string;
    draft: CreatorDraft | null;
    failed: boolean;
  } | null>(null);
  const [resolvedCreatorDraft, setResolvedCreatorDraft] = useState<CreatorDraft | null>(null);
  const [handoffAttempt, setHandoffAttempt] = useState(0);
  const [creatorProblem, setCreatorProblem] = useState<{ token: string; reason: string } | null>(null);
  const submittingRef = useRef(false);
  // Tag resolution with its token so a URL change blocks Create immediately,
  // before the next effect runs, and late responses cannot seed another token.
  const currentHandoff = handoffState?.token === handoffToken ? handoffState : null;
  const currentCreatorProblem = creatorProblem?.token === handoffToken ? creatorProblem : null;
  const handoffFailed = handoffToken !== null && !!currentHandoff?.failed;
  const handoffResolved = handoffToken === null || !!currentHandoff?.draft;
  const recoveryCreator = resolvedCreatorDraft?.token === handoffToken ? resolvedCreatorDraft.creator : null;
  const recoveryResultUrl = recoveryCreator
    ? `/quiz/results?id=${encodeURIComponent(recoveryCreator.responseId)}&type=${recoveryCreator.quizType}`
    : null;

  useEffect(() => {
    if (!retakeOf) return;
    let cancelled = false;
    getGroup(retakeOf).then((g) => {
      if (cancelled || !g) return;
      const base = g.name?.trim() || 'Group';
      setRetakeOriginalName(g.name?.trim() || null);
      // Preserve the original locked question set so the retake group is
      // an apples-to-apples comparison even if the canonical
      // getLockedQuestionIds list has drifted since the original group.
      if (Array.isArray(g.questionIds) && g.questionIds.length > 0) {
        setRetakeQuestionIds(g.questionIds);
      }
      // Only prefill if the user hasn't already typed something.
      setGroupName((current) => current || `${base} (retake)`);
    }).catch(() => { /* silent: banner just won't show the old name */ });
    return () => { cancelled = true; };
  }, [retakeOf]);

  // A consumed handoff is kept briefly in session storage for reload recovery.
  // The helper deduplicates Strict Mode's concurrent consumes; this cancellation
  // guard prevents stale resolutions from changing a different creation form.
  useEffect(() => {
    if (handoffToken === null) return;
    let cancelled = false;
    resolveCreatorDraft(handoffToken)
      .then((draft) => {
        if (cancelled) return;
        setHandoffState({ token: handoffToken, draft, failed: false });
        setResolvedCreatorDraft(draft);
        if (draft.creator.ideologyName) {
          setGroupName((current) => current || `My ${draft.creator.ideologyName} crew`);
        }
      })
      .catch(() => {
        if (!cancelled) setHandoffState({ token: handoffToken, draft: null, failed: true });
      });
    return () => { cancelled = true; };
  }, [handoffToken, handoffAttempt]);

  const handleCreate = async () => {
    if (submittingRef.current || !handoffResolved || currentCreatorProblem) return;
    if (!hostName.trim()) {
      setError('Pick a display name first.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      // Recheck identity and expiry at submit time, including after a sign-out
      // or a long-lived tab. Failure keeps this seeded attempt recoverable.
      let draft: CreatorDraft | null = null;
      if (handoffToken !== null) {
        try {
          draft = await resolveCreatorDraft(handoffToken);
        } catch {
          setHandoffState({ token: handoffToken, draft: null, failed: true });
          submittingRef.current = false;
          setSubmitting(false);
          return;
        }
      }
      const creatorLink = draft?.creator;
      // Retake mode reuses the original group's locked questionIds so any
      // longitudinal comparison is on the same question set. Otherwise: a
      // long-quiz creator gets the 36 Phase-1 core questions (richer
      // surface); everyone else gets the 12 short-quiz questions. Computed
      // here in the browser where the TSV loads reliably.
      const questionIds =
        retakeQuestionIds ??
        (creatorLink?.quizType === 'long'
          ? await getLongQuizComparisonIds(locale)
          : await getLockedQuestionIds(locale));
      const candidateAttribution = retakeOf
        ? { parentGroupId: retakeOf, creationSource: 'group_retake' }
        : draft ? draft.attribution
          : searchParams.has('source') || searchParams.has('parent')
            ? { parentGroupId: searchParams.get('parent') ?? undefined, creationSource: searchParams.get('source') }
            : { creationSource: 'direct' };
      const { groupId, attribution } = await createGroup({
        attribution: parseCreationAttribution(candidateAttribution),
        name: compareMode === 'group' ? groupName.trim() || undefined : undefined,
        hostDisplayName: hostName.trim().slice(0, GROUP_DISPLAY_NAME_MAX),
        questionIds,
        locale,
        // A 1:1 is one-to-many: the host vs each person who opens the link,
        // uncapped. A group is a fixed N-way compare (default).
        ...(compareMode === 'pair' ? { mode: 'pair' as const } : {}),
        // Persist the back-pointer so the new group's reveal can render
        // a "compared to last time" overlay.
        ...(retakeOf ? { retakeOf } : {}),
        // Link the creator's existing result (from the ?h= handoff) so the
        // server marks their slot completed and writes their locked answers.
        ...(creatorLink?.responseId
          ? {
              creatorPersonalResultId: creatorLink.responseId,
              creatorQuizType: creatorLink.quizType,
            }
          : {}),
      });
      if (draft) clearCreatorDraft(draft);
      capturePosthogEvent(posthog, 'group_created', {
        group_id: groupId,
        has_name: !!groupName.trim(),
        notify_email_provided: !!email.trim(),
        linked_creator: !!creatorLink?.responseId,
        ...(attribution ? { creation_source: attribution.creationSource, parent_group_id: attribution.parentGroupId } : {}),
      }, { sendToServer: true });
      // Best-effort: capture the host's notify email. Failures don't block.
      if (looksLikeEmail(email)) {
        await saveGroupNotificationInterest({
          groupId,
          email,
          source: 'host_create',
        });
      }
      router.push(`/group/${groupId}`);
    } catch (err) {
      debugError('createGroup failed:', err);
      if (handoffToken !== null && err instanceof GroupCreationError && err.status === 422
        && (err.reason === 'creator_result_missing' || err.reason === 'creator_answers_missing')) {
        setCreatorProblem({ token: handoffToken, reason: err.reason });
      } else {
        setError('Could not create the group. Please try again.');
      }
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <PageShell maxWidth="md">
      {/* Header */}
      <div className="text-center mb-8 mt-4">
        <div className="inline-flex items-center gap-2 bg-[#2a4173] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 uppercase tracking-wider">
          New compare
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-foreground">
          Compare with friends
        </h1>
        <p className="text-foreground/60 mt-3 max-w-md mx-auto">
          See exactly where your worldview lines up with your friends&apos;, and
          where it splits. You&apos;ll get an invite link to share.
        </p>
      </div>

      <div className="bg-white dark:bg-foreground/[0.07] rounded-2xl shadow-lg p-8">
        {retakeOf && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100 text-sm text-foreground/80">
            <p className="font-semibold text-slate-800 mb-1">{t('retake.title', locale)}</p>
            {t('retake.body.prefix', locale)}
            {retakeOriginalName ? <> <strong>{retakeOriginalName}</strong></> : <> {t('retake.body.unknown', locale)}</>}.
            {' '}
            {retakeQuestionIds ? t('retake.body.suffix', locale) : t('retake.body.suffix_approx', locale)}
          </div>
        )}

        <label className="block mb-5" htmlFor="new-host-name">
          <span className="block text-sm font-semibold text-foreground/80 mb-2">
            Your display name
          </span>
          <input
            id="new-host-name"
            type="text"
            value={hostName}
            onChange={(e) => setHostName(e.target.value)}
            maxLength={GROUP_DISPLAY_NAME_MAX}
            placeholder="What should friends call you?"
            autoComplete="nickname"
            aria-required="true"
            aria-invalid={!!error && !hostName.trim()}
            aria-describedby={error ? 'new-group-error' : undefined}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 transition"
          />
        </label>

        {/* 1:1 (one-to-many) vs group choice (compare redesign). */}
        <div className="mb-5">
          <span className="block text-sm font-semibold text-foreground/80 mb-2">
            How do you want to compare?
          </span>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Compare type">
            {(
              [
                { key: 'pair', title: 'One-on-one', sub: 'each person vs you' },
                { key: 'group', title: 'A group', sub: `everyone, up to ${GROUP_MAX_MEMBERS}` },
              ] as const
            ).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setCompareMode(opt.key)}
                aria-pressed={compareMode === opt.key}
                className={`flex flex-col items-start px-4 py-3 rounded-xl border-2 transition text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${
                  compareMode === opt.key
                    ? 'border-slate-700 bg-slate-50 dark:bg-foreground/10'
                    : 'border-gray-200 hover:border-slate-300'
                }`}
              >
                <span className="font-semibold text-foreground">{opt.title}</span>
                <span className="text-xs text-foreground/60 mt-0.5">{opt.sub}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-foreground/60 mt-2">
            {compareMode === 'pair'
              ? 'Share one link. Everyone who opens it runs a 1:1 with you, and you see them all in one place.'
              : 'A fixed group where everyone compares with everyone.'}
          </p>
        </div>

        {compareMode === 'group' && (
          <label className="block mb-5" htmlFor="new-group-name">
            <span className="block text-sm font-semibold text-foreground/80 mb-2">
              Group name <span className="text-foreground/60 font-normal">(optional)</span>
            </span>
            <input
              id="new-group-name"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={60}
              placeholder={seedArchetype ? `e.g. My ${seedArchetype} crew` : 'e.g. The Cousins'}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 transition"
            />
          </label>
        )}

        <label className="block mb-6" htmlFor="new-host-email">
          <span className="block text-sm font-semibold text-foreground/80 mb-2">
            Email me when the reveal is ready <span className="text-foreground/60 font-normal">(optional)</span>
          </span>
          <input
            id="new-host-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 transition"
          />
        </label>

        {error && (
          <div
            id="new-group-error"
            role="alert"
            aria-live="polite"
            className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm"
          >
            {error}
          </div>
        )}

        {currentCreatorProblem && (
          <div role="alert" className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <p>{currentCreatorProblem.reason === 'creator_answers_missing'
              ? 'Your saved result has no answers for this comparison. You can start a new compare and take the quiz again.'
              : 'Your saved result is no longer available. You can start a new compare and take the quiz again.'}</p>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- Restart the form with a document navigation; same-page client routing can retain the handoff query. */}
            <a href="/group/new" className="mt-3 inline-block font-semibold underline">Start without a saved result</a>
          </div>
        )}

        {handoffFailed && (
          <div role="alert" className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            <p>We couldn&apos;t load your saved result. This link may have expired or already been used. Try loading it again, or start a compare without a saved result.</p>
            <div className="mt-3 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={() => { setHandoffState(null); setHandoffAttempt((attempt) => attempt + 1); }}
                className="font-semibold underline rounded focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                Try loading again
              </button>
              {recoveryResultUrl && <Link href={recoveryResultUrl} className="font-semibold underline">Return to my result</Link>}
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- Restart the form with a document navigation; same-page client routing can retain the handoff query. */}
              <a href="/group/new" className="underline">Start without a saved result</a>
            </div>
          </div>
        )}

        <Button
          onClick={handleCreate}
          disabled={submitting || !handoffResolved || !!currentCreatorProblem}
          size="xl"
          fullWidth
        >
          {submitting
            ? 'Creating…'
            : handoffFailed || currentCreatorProblem
              ? 'Saved result unavailable'
              : !handoffResolved
                ? 'Loading your result…'
                : compareMode === 'pair'
                  ? 'Create 1:1 compare'
                  : 'Create compare'}
        </Button>

        <p className="text-xs text-foreground/60 text-center mt-6">
          Comparing can surface political views. Invite people you talk politics
          with comfortably.
        </p>
      </div>

      <div className="mt-6 text-center">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to home
        </Link>
      </div>
    </PageShell>
  );
}
